import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  FaStore, 
  FaHospital, 
  FaGraduationCap, 
  FaChartLine,
  FaIndustry,
  FaTruck,
  FaArrowRight,
  FaCheck
} from 'react-icons/fa';
import { HiSparkles, HiLightningBolt, HiCube } from 'react-icons/hi';
import './Solutions.css';

const Solutions = () => {
  const [solutions, setSolutions] = useState([]);

  useEffect(() => {
    fetchSolutions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSolutions = async () => {
    try {
      const response = await axios.get('/api/solutions');
      setSolutions(response.data.data);
    } catch (error) {
      console.error('Error fetching solutions:', error);
      setSolutions(defaultSolutions);
    }
  };

  const defaultSolutions = [
    {
      _id: '1',
      title: 'E-Commerce Solutions',
      description: 'End-to-end digital commerce platforms with seamless user experiences, secure payments, and intelligent inventory management.',
      icon: 'FaStore',
      features: ['Custom storefronts', 'Payment integration', 'Inventory management', 'Analytics dashboard']
    },
    {
      _id: '2',
      title: 'Healthcare Technology',
      description: 'HIPAA-compliant healthcare solutions that streamline patient care, records management, and medical workflows.',
      icon: 'FaHospital',
      features: ['EHR/EMR systems', 'Telemedicine platforms', 'Patient portals', 'Medical billing']
    },
    {
      _id: '3',
      title: 'EdTech Platforms',
      description: 'Innovative learning management systems and educational tools that transform how knowledge is delivered and consumed.',
      icon: 'FaGraduationCap',
      features: ['LMS development', 'Virtual classrooms', 'Assessment tools', 'Progress tracking']
    },
    {
      _id: '4',
      title: 'FinTech Solutions',
      description: 'Secure financial technology applications for banking, trading, and financial management with robust compliance.',
      icon: 'FaChartLine',
      features: ['Payment processing', 'Trading platforms', 'Risk management', 'Regulatory compliance']
    },
    {
      _id: '5',
      title: 'Manufacturing & IoT',
      description: 'Smart factory solutions with IoT integration, predictive maintenance, and real-time production monitoring.',
      icon: 'FaIndustry',
      features: ['IoT integration', 'Predictive maintenance', 'Supply chain', 'Quality control']
    },
    {
      _id: '6',
      title: 'Logistics & Supply Chain',
      description: 'Comprehensive logistics management with route optimization, real-time tracking, and warehouse automation.',
      icon: 'FaTruck',
      features: ['Fleet management', 'Route optimization', 'Warehouse systems', 'Real-time tracking']
    }
  ];

  const iconMap = {
    FaStore: <FaStore />,
    FaHospital: <FaHospital />,
    FaGraduationCap: <FaGraduationCap />,
    FaChartLine: <FaChartLine />,
    FaIndustry: <FaIndustry />,
    FaTruck: <FaTruck />
  };

  const gradients = [
    'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
    'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
    'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
    'linear-gradient(135deg, #f59e0b 0%, #ec4899 100%)',
    'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)'
  ];

  const displaySolutions = solutions.length > 0 ? solutions : defaultSolutions;

  return (
    <div className="solutions-page">
      <Navbar />

      {/* Hero Section */}
      <section className="solutions-hero">
        <div className="hero-bg">
          <div className="hero-gradient-orb orb-1"></div>
          <div className="hero-gradient-orb orb-2"></div>
          <div className="hero-gradient-orb orb-3"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="section-badge">
              <HiSparkles />
              Industry Solutions
            </div>
            <h1 className="hero-title">
              Tailored Solutions for <span className="gradient-text">Every Industry</span>
            </h1>
            <p className="hero-description">
              Specialized technology solutions designed for the unique challenges 
              and opportunities in your industry vertical.
            </p>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="section solutions-grid-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <HiCube />
              Our Expertise
            </div>
            <h2 className="section-title">
              Domain-Specific <span className="gradient-text">Excellence</span>
            </h2>
            <p className="section-description">
              Deep industry knowledge combined with cutting-edge technology 
              to deliver solutions that make a real difference.
            </p>
          </div>

          <div className="solutions-grid">
            {displaySolutions.map((solution, index) => (
              <div 
                key={solution._id} 
                className="solution-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="solution-card-inner">
                  <div 
                    className="solution-icon"
                    style={{ background: gradients[index % gradients.length] }}
                  >
                    {iconMap[solution.icon] || <FaStore />}
                    <div 
                      className="icon-glow" 
                      style={{ background: gradients[index % gradients.length] }}
                    ></div>
                  </div>
                  
                  <h3 className="solution-title">{solution.title}</h3>
                  <p className="solution-description">{solution.description}</p>
                  
                  {solution.features && solution.features.length > 0 && (
                    <ul className="solution-features">
                      {solution.features.map((feature, idx) => (
                        <li key={idx}>
                          <FaCheck className="check-icon" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}

                  <Link to="/contact" className="solution-link">
                    Learn More
                    <FaArrowRight />
                  </Link>
                </div>
                <div className="card-border-glow"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section why-section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <HiLightningBolt />
              Why Cascade
            </div>
            <h2 className="section-title">
              The <span className="gradient-text">Cascade Advantage</span>
            </h2>
          </div>

          <div className="advantages-grid">
            <div className="advantage-card">
              <div className="advantage-number">01</div>
              <h3>Industry Expertise</h3>
              <p>Our teams bring deep domain knowledge from years of working within specific industries.</p>
            </div>
            <div className="advantage-card">
              <div className="advantage-number">02</div>
              <h3>Proven Track Record</h3>
              <p>500+ successful projects delivered across multiple industry verticals worldwide.</p>
            </div>
            <div className="advantage-card">
              <div className="advantage-number">03</div>
              <h3>Scalable Architecture</h3>
              <p>Solutions built on modern, scalable foundations that grow with your business.</p>
            </div>
            <div className="advantage-card">
              <div className="advantage-number">04</div>
              <h3>Ongoing Partnership</h3>
              <p>We don't just deliver and leave – we're committed to your long-term success.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="cta-bg">
          <div className="cta-gradient"></div>
        </div>
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">
              Let's Build Your <span className="gradient-text">Industry Solution</span>
            </h2>
            <p className="cta-description">
              Tell us about your industry challenges and let's create something remarkable together.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-glow">
                Start a Conversation
                <FaArrowRight />
              </Link>
              <Link to="/services" className="btn btn-secondary">
                Explore Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Solutions;
